import {OrderService} from "../../services/OrderService";
import {CurrentUserService} from "../../../services/user/CurrentUserService";
import {FeatureNames} from "../../../types/UserConfig";
import {BehaviorSubject, Observable, Scheduler, Subject} from "rxjs";
import {ClientEventService} from "../../../services/client-events/ClientEventService";
import {isOrderClientEvent} from "../../../services/client-events/models/ClientEventRecord";
import {OrderRecord, OrderRecords} from "../../models/OrderRecord";
import "rxjs/add/observable/forkJoin";
import {isIressOrder} from "../../models/iress/IressOrderRecord";
import {isSimulatedOrder} from "../../models/simulated/SimulatedOrderRecord";
import {AccountStore} from "../../../account/store/AccountStore";
import {AccountBriefRecord} from "../../../account/types/AccountBriefRecord";

const OrderUpdateIntervalSeconds = 15;
const OrderUpdateInterval = OrderUpdateIntervalSeconds * 1000;

export class OrderStore {

    public static readonly id = "orderStore";

    private readonly orders$ = new BehaviorSubject<OrderRecords>([]);
    private readonly currentPortfolioOrders$: Observable<OrderRecords>;
    private readonly ordersUpdated$ = new Subject();

    private readonly stop$: Observable<boolean>;

    /** @ngInject */
    public constructor(private readonly orderService: OrderService,
                       private readonly clientEventService: ClientEventService,
                       private readonly accountStore: AccountStore,
                       private readonly currentUserService: CurrentUserService) {
        this.stop$ = this.buildStopPolling();
        if (this.currentUserService.hasFeature(FeatureNames.SCREEN_EQUITY_ORDERS, FeatureNames.SCREEN_OPTION_ORDERS)) {
            this.init();
            this.currentPortfolioOrders$ = this.buildCurrentPortfolioOrders();
        } else {
            this.currentPortfolioOrders$ = new BehaviorSubject([]);
        }
    }

    private buildStopPolling(): Observable<boolean> {
        return this.currentUserService
                   .current
                   .map((c) => c.isLoggedIn)
                   .distinctUntilChanged()
                   .filter((loggedIn) => !loggedIn);
    }

    private init() {
        this.startUpdateInterval();
        this.watchOrderEvents();
        this.updateOrders();
    }

    private watchOrderEvents() {
        this.clientEventService
            .events()
            .filter(isOrderClientEvent)
            .auditTime(1000)
            .takeUntil(this.stop$)
            .subscribe(() => this.updateOrders());
    }

    private startUpdateInterval() {
        const delay = Observable.of(1).delay(OrderUpdateInterval);
        this.ordersUpdated$
            .observeOn(Scheduler.async)
            .switchMap(() => delay)
            .takeUntil(this.stop$)
            .subscribe(() => this.updateOrders());
    }

    private updateOrders() {
        const market$ = this.retrieveMarketOrder();
        const simulated$ = this.retrieveSimulatedOrders();

        Observable.forkJoin(market$, simulated$)
                  .map(([market, simulated]) => [...market, ...simulated])
                  .withLatestFrom(this.orders$)
                  .filter(([refreshed, previous]) => this.ordersHasChanged(refreshed, previous))
                  .map(([refreshed, _]) => refreshed)
                  .finally(() => this.ordersUpdated$.next(true))
                  .subscribe((orders) => this.orders$.next(orders));
    }

    private buildCurrentPortfolioOrders(): Observable<OrderRecords> {
        return Observable.combineLatest(this.orders$, this.accountStore.current)
                         .map(([orders, portfolio]) => this.portfolioOrders(orders, portfolio))
                         .startWith([])
                         .publishReplay(1)
                         .refCount();
    }

    private retrieveMarketOrder(): Observable<OrderRecords> {
        const request = this.orderService.openOrders();
        return Observable.fromPromise(request)
                         .catch(() => Observable.empty());
    }

    private retrieveSimulatedOrders(): Observable<OrderRecords> {
        const request = this.orderService.simulatedOrders();
        return Observable.fromPromise(request)
                         .catch(() => Observable.empty());
    }

    private ordersHasChanged(refreshedOrders: OrderRecords, previousOrders: OrderRecords): boolean {
        if (refreshedOrders.length !== previousOrders.length) {
            return true;
        }

        const previousOrdersMap = new Map<string, OrderRecord>();

        previousOrders.forEach((o) => previousOrdersMap.set(this.hashKey(o), o));

        // For every refreshed order, find the previous one and then check if they have changed
        return refreshedOrders.some((o) => {
            const orderKey = this.hashKey(o);
            const previous = previousOrdersMap.get(orderKey);
            if (previous == null) {
                return true;
            } else {
                return o.updated !== previous.updated ||
                    o.lastAction !== previous.lastAction ||
                    o.actionStatus !== previous.actionStatus ||
                    o.state !== previous.state;
            }
        });
    }

    private portfolioOrders(orders: OrderRecords, account: AccountBriefRecord | null): OrderRecords {
        return orders.filter((o) => o.accountNumber === account?.number);
    }

    private hashKey(orderRecord: OrderRecord): string {
        if (isIressOrder(orderRecord)) {
            return `IRESS:${orderRecord.iosServer}:${orderRecord.orderNumber}`;
        } else if (isSimulatedOrder(orderRecord)) {
            return `SIMULATED:${orderRecord.id}`;
        } else {
            return "";
        }
    }

    public get orders(): Observable<OrderRecords> {
        return this.orders$
                   .observeOn(Scheduler.async);
    }

    public get currentPortfolioOrders(): Observable<OrderRecords> {
        return this.currentPortfolioOrders$
                   .observeOn(Scheduler.async);
    }
}
